import { motion } from 'framer-motion'
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts'
import { usePriceHistory } from '../../hooks/usePriceHistory'

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  itemId:      string
  itemName:    string
  itemEmoji:   string
  householdId: string
  isPremium:   boolean
}

// ─── Trend badge color ────────────────────────────────────────────────────────

function trendColor(pct: number | null): { text: string; label: string } {
  if (pct === null) return { text: '#9A9491', label: '—' }
  if (pct > 5)   return { text: '#C25B4E', label: `↑ ${pct.toFixed(1)}%` }
  if (pct > 0)   return { text: '#D4863A', label: `↑ ${pct.toFixed(1)}%` }
  if (pct < -5)  return { text: '#6B9E78', label: `↓ ${Math.abs(pct).toFixed(1)}%` }
  if (pct < 0)   return { text: '#6B9E78', label: `↓ ${Math.abs(pct).toFixed(1)}%` }
  return { text: '#9A9491', label: 'Stable' }
}

// ─── PriceHistoryChart ────────────────────────────────────────────────────────

export default function PriceHistoryChart({
  itemId,
  itemName: _itemName,
  itemEmoji: _itemEmoji,
  householdId,
  isPremium,
}: Props) {
  const {
    priceHistory,
    chartData,
    avgPrice,
    lowestPrice,
    highestPrice,
    priceChangePct,
    isLoading,
  } = usePriceHistory(itemId, householdId)

  // ── Pantry + gate ──────────────────────────────────────────────────────────

  if (!isPremium) {
    return (
      <div className="relative rounded-2xl overflow-hidden border border-[#E8E2DC]">
        <div className="blur-sm pointer-events-none select-none p-5">
          <div className="h-32 bg-[#F2EDE6] rounded-xl" />
        </div>
        <div className="absolute inset-0 bg-white/70 flex flex-col items-center justify-center gap-2 px-4">
          <span className="text-2xl">📈</span>
          <p className="text-sm font-semibold text-[#2C2A26] text-center">
            Upgrade to see price history
          </p>
          <p className="text-xs text-[#7A7468] text-center">
            Track personal grocery inflation over time
          </p>
        </div>
      </div>
    )
  }

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="flex gap-2">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="flex-1 h-10 rounded-xl bg-[#F0EDE8]" />
          ))}
        </div>
        <div className="h-40 rounded-2xl bg-[#F0EDE8]" />
      </div>
    )
  }

  // ── Not enough data ────────────────────────────────────────────────────────

  if (chartData.length < 2) {
    return (
      <div className="text-center py-6 bg-[#FAF8F4] rounded-2xl border border-[#E8E2DC]">
        <span className="text-3xl">📈</span>
        <p className="text-sm font-medium text-[#2C2A26] mt-2">Not enough price data yet</p>
        <p className="text-xs text-[#7A7468] mt-1">
          Log prices when you confirm items to see trends over time
        </p>
      </div>
    )
  }

  const trend = trendColor(priceChangePct)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-3">

      {/* Stats pills */}
      <div className="grid grid-cols-4 gap-1.5">
        {[
          {
            label: 'avg',
            value: avgPrice != null ? `$${avgPrice.toFixed(2)}` : '—',
            bg:    '#F2EDE6',
            color: '#7A7468',
          },
          {
            label: 'lowest',
            value: lowestPrice != null ? `$${lowestPrice.toFixed(2)}` : '—',
            bg:    '#E8F2EA',
            color: '#6B9E78',
          },
          {
            label: 'highest',
            value: highestPrice != null ? `$${highestPrice.toFixed(2)}` : '—',
            bg:    '#FAEAE8',
            color: '#C25B4E',
          },
          {
            label: 'trend',
            value: trend.label,
            bg:    '#F2EDE6',
            color: trend.text,
          },
        ].map((pill, i) => (
          <motion.div
            key={pill.label}
            className="rounded-xl px-2 py-2 text-center"
            style={{ background: pill.bg }}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05, duration: 0.2 }}
          >
            <p className="text-[10px] text-[#9A9491] mb-0.5">{pill.label}</p>
            <p className="text-xs font-bold" style={{ color: pill.color }}>
              {pill.value}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Line chart */}
      <div className="bg-white rounded-2xl border border-[#E8E2DC] p-3">
        <ResponsiveContainer width="100%" height={160}>
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 8, bottom: 0, left: -20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#E8E2DC"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 10, fill: '#B0A99F' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#B0A99F' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => `$${v.toFixed(0)}`}
              domain={['auto', 'auto']}
            />
            <Tooltip
              formatter={(value: unknown) => [`$${(value as number).toFixed(2)}`, 'Price']}
              contentStyle={{
                background:   'white',
                border:       '1px solid #E8E2DC',
                borderRadius: '12px',
                fontSize:     '12px',
              }}
            />
            {avgPrice != null && (
              <ReferenceLine
                y={avgPrice}
                stroke="#B0A99F"
                strokeDasharray="4 4"
                label={{ value: 'avg', fontSize: 9, fill: '#B0A99F', position: 'insideTopRight' }}
              />
            )}
            <Line
              type="monotone"
              dataKey="price"
              stroke="#6B9E78"
              strokeWidth={2}
              dot={{ r: 4, fill: '#6B9E78', strokeWidth: 0 }}
              activeDot={{ r: 6, fill: '#6B9E78' }}
              isAnimationActive={true}
              animationDuration={800}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent price list */}
      {priceHistory.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E8E2DC] divide-y divide-[#F0EDE8] overflow-hidden">
          {[...priceHistory].reverse().slice(0, 5).map((record, i) => {
            const price = record.price_paid as number
            const priceColor = avgPrice == null
              ? '#2C2A26'
              : price < avgPrice * 0.98 ? '#6B9E78'
              : price > avgPrice * 1.02 ? '#C25B4E'
              : '#9A9491'
            return (
              <div key={record.id ?? i} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-[#9A9491]">
                  {new Date(record.confirmed_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric',
                  })}
                </span>
                {record.store_name && (
                  <span className="text-xs text-[#B0A99F] flex-1 text-center">
                    {record.store_name}
                  </span>
                )}
                <span className="text-sm font-semibold" style={{ color: priceColor }}>
                  ${price.toFixed(2)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
